import React, { useState, useContext } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Search, ArrowRight, Check, Menu, X, Mail, Compass, ChevronRight, Zap, Shield, Code, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AuthContext } from "@/App";
import { RecentReviews } from "@/components/Reviews";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import Footer from "@/components/Footer";
import InstallPrompt from "@/components/InstallPrompt";
import { useTranslation } from "react-i18next";

const LandingPage = () => {
  const [prompt, setPrompt] = useState("");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { t } = useTranslation();

  const handleSearch = (e) => {
    e.preventDefault();
    if (prompt.trim()) {
      navigate(`/search?q=${encodeURIComponent(prompt.trim())}`);
    }
  };

  const examplePrompts = [
    "Build a mobile app with Swift and SwiftUI",
    "Generate SEO-optimized blog posts",
    "Create product images for e-commerce",
    "Debug my Python Django application"
  ];

  const tiers = [
    {
      name: "Free", price: "$0", period: "/month",
      features: ["10 searches/month", "Basic prompts", "English only", "30-day history"],
      highlight: false
    },
    {
      name: "Pro", price: "$15", period: "/month",
      features: ["Unlimited searches", "Advanced prompts", "Chat with Promptviser", "Forever history", "Email support"],
      highlight: true, popular: true, yearly: "$150/year (save 17%)"
    },
    {
      name: "Premium", price: "$35", period: "/month",
      features: ["Mega prompts (CoT)", "Agentic + Programmatic prompts", "SEO tools", "News feed", "18 languages", "Priority support"],
      highlight: false, yearly: "$350/year (save 17%)"
    }
  ];

  return (
    <div className="min-h-screen bg-dark relative overflow-hidden">
      <div className="fixed inset-0 grid-bg opacity-40" />
      <div className="hero-glow top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2" />

      {/* Navigation */}
      <nav className="relative z-50 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2.5" data-testid="logo-link">
              <div className="w-9 h-9 rounded-lg bg-purple-700 flex items-center justify-center relative">
                <Compass className="w-5 h-5 text-white" />
                <div className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-neon animate-neon-pulse" />
              </div>
              <span className="font-inter font-bold text-xl tracking-tight">promptviser</span>
            </Link>

            <div className="hidden md:flex items-center gap-6">
              <Link to="/pricing" className="text-white/60 hover:text-white transition-colors text-sm font-medium">{t("nav.pricing")}</Link>
              <Link to="/faq" className="text-white/60 hover:text-white transition-colors text-sm font-medium">{t("nav.faq")}</Link>
              <Link to="/contact" className="text-white/60 hover:text-white transition-colors text-sm font-medium" data-testid="nav-contact-link">{t("footer.contact")}</Link>
              <LanguageSwitcher />
              {user ? (
                <>
                  <Link to="/dashboard">
                    <Button className="bg-purple-700 text-white hover:bg-purple-600 font-semibold px-6" data-testid="dashboard-btn">
                      {t("nav.dashboard")}
                    </Button>
                  </Link>
                </>
              ) : (
                <div className="flex items-center gap-3">
                  <Link to="/login" className="text-white/60 hover:text-white transition-colors text-sm font-medium">{t("nav.login")}</Link>
                  <Link to="/register">
                    <Button className="bg-purple-700 text-white hover:bg-purple-600 font-semibold px-6" data-testid="get-started-btn">
                      {t("nav.register")}
                    </Button>
                  </Link>
                </div>
              )}
            </div>

            <button className="md:hidden text-white" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-toggle">
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {mobileMenuOpen && (
            <div className="md:hidden pt-4 pb-2 border-t border-white/10 mt-4 space-y-3">
              <Link to="/pricing" className="block text-white/60 hover:text-white py-2">Pricing</Link>
              <Link to="/faq" className="block text-white/60 hover:text-white py-2">FAQ</Link>
              {user ? (
                <Link to="/dashboard"><Button className="w-full bg-purple-700 text-white">Dashboard</Button></Link>
              ) : (
                <>
                  <Link to="/login" className="block text-white/60 hover:text-white py-2">Log in</Link>
                  <Link to="/register"><Button className="w-full bg-purple-700 text-white">Get Started</Button></Link>
                </>
              )}
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-20 md:pt-32 pb-20">
        <div className="text-center max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-purple-500/30 bg-purple-500/5 mb-8 animate-fade-in-up">
            <div className="neon-dot" />
            <span className="text-sm font-medium text-purple-300">{t("landing.hero_badge")}</span>
          </div>

          <h1 className="font-inter font-black text-4xl sm:text-5xl lg:text-7xl mb-6 animate-fade-in-up stagger-1 leading-tight">
            {t("landing.hero_1")}
            <span className="block text-gradient-purple">{t("landing.hero_2")}</span>
            {t("landing.hero_3")}
          </h1>

          <p className="text-base md:text-lg text-white/50 mb-12 max-w-2xl mx-auto animate-fade-in-up stagger-2">
            {t("landing.hero_subtitle")}
          </p>

          <form onSubmit={handleSearch} className="max-w-3xl mx-auto animate-fade-in-up stagger-3">
            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-r from-purple-600/20 to-neon/5 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative flex items-center bg-dark-paper border border-white/10 rounded-xl overflow-hidden">
                <Search className="w-6 h-6 text-white/30 ml-5" />
                <Input
                  type="text" value={prompt} onChange={(e) => setPrompt(e.target.value)}
                  placeholder={t("landing.placeholder")}
                  className="flex-1 bg-transparent border-0 text-lg py-6 px-4 placeholder:text-white/25 focus-visible:ring-0 focus-visible:ring-offset-0"
                  data-testid="hero-search-input"
                />
                <Button type="submit" className="m-2 bg-purple-700 text-white hover:bg-purple-600 font-semibold px-6 py-6" data-testid="hero-search-button">
                  <span className="hidden sm:inline mr-2">{t("landing.cta_search")}</span>
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </div>
            </div>
          </form>

          <div className="mt-8 flex flex-wrap justify-center gap-3 animate-fade-in-up stagger-4">
            <span className="text-white/30 text-sm">Try:</span>
            {examplePrompts.map((example, i) => (
              <button
                key={i}
                onClick={() => { setPrompt(example); navigate(`/search?q=${encodeURIComponent(example)}`); }}
                className="text-sm text-white/50 hover:text-purple-300 border border-white/10 hover:border-purple-500/30 px-3 py-1.5 rounded-full transition-all"
                data-testid={`example-prompt-${i}`}
              >
                {example}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="relative z-10 py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-xs uppercase tracking-[0.2em] text-neon font-semibold">{t("landing.how_label")}</span>
            <h2 className="font-inter font-bold text-2xl md:text-3xl mt-4">{t("landing.how_title")}</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: <Search className="w-6 h-6" />, title: t("landing.step1_t"), description: t("landing.step1_d") },
              { icon: <Code className="w-6 h-6" />, title: t("landing.step2_t"), description: t("landing.step2_d") },
              { icon: <Zap className="w-6 h-6" />, title: t("landing.step3_t"), description: t("landing.step3_d") }
            ].map((step, i) => (
              <div key={i} className="group p-8 bg-dark-paper border border-white/10 rounded-xl card-hover" data-testid={`how-it-works-step-${i}`}>
                <div className="w-12 h-12 rounded-lg bg-purple-900/50 border border-purple-500/20 flex items-center justify-center text-purple-400 mb-6 group-hover:bg-purple-700 group-hover:text-white transition-colors">
                  {step.icon}
                </div>
                <h3 className="font-inter font-semibold text-lg mb-3">{step.title}</h3>
                <p className="text-white/50 leading-relaxed text-sm">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Preview */}
      <section className="relative z-10 py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-xs uppercase tracking-[0.2em] text-neon font-semibold">Pricing</span>
            <h2 className="font-inter font-bold text-2xl md:text-3xl mt-4">Simple, Transparent Pricing</h2>
            <p className="text-white/40 mt-3 text-sm">Higher tiers include ALL lower tier features. Save 17% with yearly billing.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {tiers.map((tier, i) => (
              <div key={i} className={`p-7 rounded-2xl border ${tier.highlight ? "border-purple-500 pricing-highlight" : "border-white/10"} bg-dark-paper relative`}>
                {tier.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-purple-700 text-white text-xs font-bold rounded-full">POPULAR</div>
                )}
                <h3 className="font-inter font-semibold text-lg mb-2">{tier.name}</h3>
                <div className="flex items-baseline gap-1 mb-1">
                  <span className="font-inter font-black text-4xl">{tier.price}</span>
                  <span className="text-white/30 text-sm">{tier.period}</span>
                </div>
                {tier.yearly && <p className="text-neon text-xs mb-4">{tier.yearly}</p>}
                {!tier.yearly && <div className="mb-4" />}
                <ul className="space-y-2.5 mb-6">
                  {tier.features.map((f, j) => (
                    <li key={j} className="flex items-center gap-2.5 text-white/60 text-sm">
                      <Check className="w-4 h-4 text-neon flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <Link to={tier.name === "Free" ? "/register" : "/pricing"}>
                  <Button className={`w-full font-semibold ${tier.highlight ? "bg-purple-700 text-white hover:bg-purple-600" : "bg-white/5 hover:bg-white/10 text-white/80"}`}>
                    {tier.name === "Free" ? "Get Started" : `Upgrade to ${tier.name}`}
                  </Button>
                </Link>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/pricing" className="text-purple-400 hover:text-purple-300 text-sm font-medium inline-flex items-center gap-1">
              {t("landing.view_all")} <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Recent Reviews */}
      <section className="relative z-10 py-16 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <RecentReviews limit={3} />
        </div>
      </section>

      {/* Footer */}
      <Footer />
      <InstallPrompt />
    </div>
  );
};

export default LandingPage;
